import { AxiosResponse } from 'axios'

import { isTokenExpired } from './token'
import {
  clearAnonymousUserStorage,
  clearRegisteredUserStorage,
  clearUsersStorage,
  getActiveUser,
  hasAnonymousUser,
  hasRegisteredUser,
  updateAnonymousUser,
  updateRegisteredUser,
} from './user'
import { refreshTokensMaxRetryCount, refreshTokensRetryDelayMs } from '../constants/common'
import { StorageIds } from '../constants/storage'
import { isDevMode } from '../envs/envs'
import { Auth } from '../service/auth'
import { TokenType } from '../types/authTypes'
import { BasicUserCredentials, UserResponseDataType } from '../types/servicesTypes/identityTypes'

let accessTokenRefreshTimerId: ReturnType<typeof setTimeout>

const clearAccessTokenRefreshTimer = () => {
  if (accessTokenRefreshTimerId) {
    clearTimeout(accessTokenRefreshTimerId)
    isDevMode && console.log('[sdk 🧨 clearAccessTokenRefreshTimer]', { accessTokenRefreshTimerId })
  }
}

export const startAccessTokenRefreshTimer = (accessTokenExpiryTime: string) => {
  clearAccessTokenRefreshTimer()

  const nowTime = new Date().getTime()
  const accessExpiryTime = new Date(accessTokenExpiryTime).getTime()
  const timeout = accessExpiryTime - nowTime

  accessTokenRefreshTimerId = setTimeout(() => {
    void refreshTokens()
  }, timeout * 0.9)
  isDevMode &&
    console.log('[sdk 🧨 startAccessTokenRefreshTimer]', { accessTokenRefreshTimerId, accessTokenExpiryTime, timeout })
}

const loginRegisteredUser = async () => {
  void refreshTokens()
}

export const removeRegisteredUser = () => {
  if (hasRegisteredUser()) {
    clearRegisteredUserStorage()
    void loginAnonymousUser()
  }
}

export const setRegisteredUser = (data: UserResponseDataType) => {
  isDevMode && console.log('[sdk setRegisteredUser]')
  localStorage.setItem(StorageIds.REGISTERED, JSON.stringify(data))
}

const updateCurrentUser = (data: UserResponseDataType) => {
  isDevMode && console.log('[sdk updateCurrentUser]')
  if (hasRegisteredUser()) {
    updateRegisteredUser(data)
  } else {
    updateAnonymousUser(data)
  }
}

const wait = (delay: number) => {
  return new Promise((resolve) => setTimeout(resolve, delay))
}
const retryRefreshTokens: (triesLeft?: number) => Promise<AxiosResponse<UserResponseDataType>> = async (
  triesLeft = refreshTokensMaxRetryCount,
) => {
  return Auth.refreshTokens().catch(async (error) => {
    triesLeft -= 1
    if (!triesLeft) {
      throw error
    }
    return wait(refreshTokensRetryDelayMs).then(() => retryRefreshTokens(triesLeft))
  })
}

let isTokenRefreshingStatus = false
const isTokensRefreshing = () => {
  return isTokenRefreshingStatus
}
const setIsTokenRefreshingStatus = (val: boolean) => {
  isTokenRefreshingStatus = val
}

export const refreshTokens = async () => {
  if (isTokensRefreshing()) {
    return
  }
  setIsTokenRefreshingStatus(true)
  return retryRefreshTokens()
    .then((res) => {
      updateCurrentUser(res.data)
      return res
    })
    .catch(() => {
      clearUsersStorage()
      auth()
    })
    .finally(() => setIsTokenRefreshingStatus(false))
}

/**
 * Function to log in an anonymous user
 *
 * @param {BasicUserCredentials} basicUserCredentials - user data for registration
 * Here we need to store BasicUserCredentials and UserResponseDataType in localStorage
 * BasicUserCredentials contains login and password
 * UserResponseDataType contains accessToken, refreshToken, etc
 * updateAnonymousUser() is used to update UserResponseDataType in localStorage with mix of BasicUserCredentials and UserResponseDataType
 */
const loginAnonymousUser = async (basicUserCredentials?: BasicUserCredentials) => {
  isDevMode && console.log('[sdk loginAnonymousUser]')
  try {
    const { data: user } = await Auth.loginAnonymousUser(basicUserCredentials)
    if (user) {
      updateAnonymousUser({ ...user, ...basicUserCredentials })
    } else {
      clearAnonymousUserStorage()
      void registerAnonymousUser()
    }
  } catch (error) {
    isDevMode && console.log(`[sdk loginAnonymous]: ${error}`)
    clearAnonymousUserStorage()
    void registerAnonymousUser()
  }
}

const registerAnonymousUser = async () => {
  isDevMode && console.log('[sdk registerAnonymousUser]')

  try {
    const credentials = await Auth.registerAnonymousUser()
    if (credentials) {
      void loginAnonymousUser(credentials)
    }
  } catch (error) {
    isDevMode && console.log(`[sdk registerAnonymousUser]: ${error}`)
  }
}

export const auth = async () => {
  isDevMode && console.log('[sdk auth]')

  if (hasRegisteredUser()) {
    if (isTokenExpired(TokenType.REFRESH)) {
      clearRegisteredUserStorage()
      void loginAnonymousUser()
    } else {
      void loginRegisteredUser()
    }
  } else if (hasAnonymousUser()) {
    if (isTokenExpired(TokenType.ACCESS)) {
      void loginAnonymousUser()
    } else {
      const user = getActiveUser()
      updateAnonymousUser(user as UserResponseDataType)
    }
  } else {
    void registerAnonymousUser()
  }
}
